/**
 * KeywordImageDisplay - Displays images related to detected keywords
 *
 * This module handles the display of images fetched from the artwork API
 * when keywords are detected in conversations.
 */

export class KeywordImageDisplay {
    constructor() {
        this.imageContainer = null;
        this.displayedImages = new Set(); // Track displayed images to avoid duplicates
        this.imageViewerModal = null; // Reference to the image viewer modal
        this.initialize();
    }

    initialize() {
        console.log("KeywordImageDisplay: Initializing...");

        // Create the image container if it doesn't exist
        this.createImageContainer();

        // Initialize the image viewer modal
        this.initializeImageViewerModal();

        console.log("KeywordImageDisplay: Initialized successfully");
    }

    /**
     * Initialize the image viewer modal
     */
    initializeImageViewerModal() {
        // Check if ImageViewerModal is available globally
        if (window.ImageViewerModal) {
            this.imageViewerModal = new window.ImageViewerModal();
            console.log("KeywordImageDisplay: Image viewer modal initialized");
        } else {
            console.warn("KeywordImageDisplay: ImageViewerModal not available");
        }
    }

    createImageContainer() {
        // Find the chat history container
        const chatHistory = document.querySelector(".chat-history");
        if (!chatHistory) {
            console.warn("KeywordImageDisplay: Chat history not found");
            return;
        }

        // Use the chat history as our container
        this.imageContainer = chatHistory;
    }

    /**
     * Display images based on keyword detection results
     * @param {Array} keywordImages - Array of keyword image results from API
     */
    displayKeywordImages(keywordImages) {
        if (!keywordImages || keywordImages.length === 0) {
            console.log("KeywordImageDisplay: No images to display");
            this.hideImageContainer();
            return;
        }

        console.log(
            "KeywordImageDisplay: Displaying images for",
            keywordImages.length,
            "keywords",
        );

        // Clear existing images
        this.clearImages();

        // Collect all unique images from all keyword results
        const allImages = [];
        keywordImages.forEach((result) => {
            const images = result.images || [];
            images.forEach((image) => {
                // Only add if not already in our collection
                if (!allImages.find((existing) => existing.url === image.url)) {
                    allImages.push({
                        ...image,
                        keyword: result.fetched_keyword,
                    });
                }
            });
        });

        // Display all images as chat bubbles
        this.displayImagesAsChatBubbles(allImages);

        // Show the container
        this.showImageContainer();

        // Start automatic slideshow in 3D space if images are available
        if (allImages.length > 0) {
            this.startAutoSlideshow(allImages);
        }
    }

    /**
     * Start automatic slideshow of images in 3D space
     * @param {Array} images - Array of image objects with url property
     */
    startAutoSlideshow(images) {
        // Get all image URLs
        const imageUrls = images.map(img => img.url).filter(url => url);

        if (imageUrls.length === 0) {
            console.log("KeywordImageDisplay: No valid image URLs for slideshow");
            return;
        }

        // Use SimpleImagePanel if available through ModelViewer
        if (window.simpleImagePanel) {
            console.log(`KeywordImageDisplay: Starting auto-slideshow with ${imageUrls.length} images`);
            window.simpleImagePanel.startSlideshow(imageUrls, 5000); // 5 seconds per image
        } else if (window.modelViewer) {
            // Initialize SimpleImagePanel through ModelViewer if not already done
            console.log("KeywordImageDisplay: Initializing SimpleImagePanel through ModelViewer");
            window.modelViewer.showImage3D(imageUrls[0], '').then(() => {
                // After first image is shown, SimpleImagePanel should be available
                if (window.simpleImagePanel && imageUrls.length > 1) {
                    // Start slideshow from the REMAINING images (skip first since already shown)
                    window.simpleImagePanel.startSlideshow(imageUrls.slice(1), 5000);
                }
            });
        } else {
            console.warn("KeywordImageDisplay: Neither SimpleImagePanel nor ModelViewer available for slideshow");
        }
    }

    /**
     * Display images as chat bubbles
     * @param {Array} images - Array of image objects
     */
    displayImagesAsChatBubbles(images) {
        if (images.length === 0) {
            return;
        }

        console.log(
            `KeywordImageDisplay: Displaying ${images.length} images as chat bubbles`,
        );

        // Create chat bubble container
        const chatBubble = document.createElement("div");
        chatBubble.className = "bot-message keyword-images-bubble";
        chatBubble.style.cssText = `
            background-color: rgba(255, 255, 255, 0.2);
            color: white;
            padding: 15px;
            border-radius: 15px;
            margin: 10px 0;
            max-width: 80%;
            word-wrap: break-word;
            animation: fadeIn 0.3s ease-in;
        `;

        // Create images grid inside the bubble
        const imagesGrid = document.createElement("div");
        imagesGrid.className = "keyword-images-grid";
        imagesGrid.style.cssText = `
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 10px;
            margin: 0;
        `;

        // Add each image
        images.forEach((image, index) => {
            const imageElement = this.createChatBubbleImage(image, index);
            if (imageElement) {
                imagesGrid.appendChild(imageElement);
            }
        });

        chatBubble.appendChild(imagesGrid);
        this.imageContainer.appendChild(chatBubble);
    }

    /**
     * Create an image element for chat bubble display
     * @param {Object} image - Image data from API
     * @param {number} index - Image index
     * @returns {HTMLElement|null} - Created image element or null
     */
    createChatBubbleImage(image, index) {
        const imageUrl = image.url;
        const filename = image.filename || "Unknown";
        const keyword = image.keyword || "Unknown";

        // Check if we've already displayed this image
        if (this.displayedImages.has(imageUrl)) {
            return null;
        }

        // Mark as displayed
        this.displayedImages.add(imageUrl);

        // Create image container
        const imageContainer = document.createElement("div");
        imageContainer.className = "keyword-image-item";
        imageContainer.style.cssText = `
            position: relative;
            border-radius: 8px;
            overflow: hidden;
            background-color: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            transition: transform 0.2s ease, box-shadow 0.2s ease;
            cursor: pointer;
        `;

        // Add hover effects
        imageContainer.addEventListener("mouseenter", () => {
            imageContainer.style.transform = "scale(1.02)";
            imageContainer.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.3)";
        });

        imageContainer.addEventListener("mouseleave", () => {
            imageContainer.style.transform = "scale(1)";
            imageContainer.style.boxShadow = "none";
        });

        // Create image element
        const img = document.createElement("img");
        img.src = imageUrl;
        img.alt = `${keyword} - ${filename}`;
        img.style.cssText = `
            width: 100%;
            height: 120px;
            object-fit: cover;
            display: block;
            border-radius: 8px;
        `;

        // Add error handling
        img.onerror = () => {
            imageContainer.style.display = "none";
            console.warn(
                `KeywordImageDisplay: Failed to load image: ${imageUrl}`,
            );
        };

        // Add click handler to open image in modal (image only, no chat)
        imageContainer.addEventListener("click", () => {
            if (this.imageViewerModal) {
                this.imageViewerModal.openModal(imageUrl, filename, keyword, true);
            } else {
                // Fallback to new tab if modal not available
                window.open(imageUrl, "_blank");
            }
        });

        imageContainer.appendChild(img);
        return imageContainer;
    }

    /**
     * Clear all displayed images
     */
    clearImages() {
        // Only clear keyword image bubbles, not the entire chat history
        if (this.imageContainer) {
            const keywordBubbles = this.imageContainer.querySelectorAll(
                ".keyword-images-bubble",
            );
            keywordBubbles.forEach((bubble) => bubble.remove());
        }
        this.displayedImages.clear();
    }

    /**
     * Show the image container
     */
    showImageContainer() {
        // Chat history is always visible, so we don't need to show/hide it
        // Just scroll to the bottom to show new images
        if (this.imageContainer) {
            this.imageContainer.scrollTop = this.imageContainer.scrollHeight;
        }
    }

    /**
     * Hide the image container
     */
    hideImageContainer() {
        // We don't hide the chat history, just clear images if needed
        this.clearImages();
    }

    /**
     * Get the current image container element
     * @returns {HTMLElement|null} - The image container element
     */
    getImageContainer() {
        return this.imageContainer;
    }
}

// Make the class available globally
window.KeywordImageDisplay = KeywordImageDisplay;
